import { ChevronsUpDown } from 'lucide-react';
import {
  useCallback,
  useId,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type KeyboardEvent,
} from 'react';
import { assignRef } from '../assign-ref.ts';
import { controlClassName, describedByOf, Field, type FieldTexts } from '../Field/Field.tsx';
import { usePlacementUnder } from '../popup-placement.ts';
import { useClosedOutside } from '../use-closed-outside.ts';
import { chooseNativeValue, OPENING_KEYS } from './select-choice.ts';
import { SelectPopup } from './SelectPopup.tsx';
import styles from './Select.module.css';

export type SelectOption = { value: string; label: string };

export type SelectProps = ComponentPropsWithRef<'select'> &
  FieldTexts & {
    options: readonly SelectOption[];
    placeholder?: string | undefined;
  };

const opensList = (event: KeyboardEvent<HTMLSelectElement>): boolean =>
  OPENING_KEYS.has(event.key) || (event.altKey && event.key === 'ArrowDown');

export const Select = ({
  label,
  options,
  placeholder,
  helperText,
  errorText,
  hideLabel,
  required,
  disabled,
  id,
  ref,
  className,
  onMouseDown,
  onKeyDown,
  ...rest
}: SelectProps) => {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const listId = `${selectId}-list`;
  const native = useRef<HTMLSelectElement | null>(null);
  const wrapper = useRef<HTMLSpanElement>(null);
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const open = openedAt !== null;
  const place = usePlacementUnder(wrapper, open);
  const attach = useCallback(
    (element: HTMLSelectElement | null) => {
      native.current = element;
      assignRef(ref, element);
    },
    [ref],
  );
  const dismiss = useCallback(() => {
    setOpenedAt(null);
  }, []);
  useClosedOutside(wrapper, open, dismiss);

  const openList = () => {
    if (native.current !== null && disabled !== true) setOpenedAt(native.current.value);
  };
  const close = () => {
    setOpenedAt(null);
    native.current?.focus();
  };
  const pick = (value: string) => {
    if (native.current !== null) chooseNativeValue(native.current, value);
    close();
  };

  return (
    <Field
      id={selectId}
      label={label}
      helperText={helperText}
      errorText={errorText}
      hideLabel={hideLabel}
      required={required}
    >
      <span ref={wrapper} className={styles['wrapper']}>
        <select
          id={selectId}
          ref={attach}
          className={controlClassName(errorText, [styles['select'], className ?? ''].join(' '))}
          aria-invalid={errorText ? true : undefined}
          aria-describedby={describedByOf(selectId, { helperText, errorText })}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          data-open={open}
          required={required}
          disabled={disabled}
          onMouseDown={(event) => {
            onMouseDown?.(event);
            if (event.defaultPrevented || event.button !== 0) return;
            event.preventDefault();
            event.currentTarget.focus();
            if (open) dismiss();
            else openList();
          }}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (event.defaultPrevented || open || !opensList(event)) return;
            event.preventDefault();
            openList();
          }}
          {...rest}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronsUpDown size={16} aria-hidden="true" className={styles['chevron']} />
        {openedAt === null ? null : (
          <SelectPopup
            id={listId}
            label={label}
            options={options}
            value={openedAt}
            place={place}
            onPick={pick}
            onClose={close}
          />
        )}
      </span>
    </Field>
  );
};
